import {
	GoogleAddressComponentsFields,
	GoogleAddressDetailsFields,
	GoogleAddressOptionFields,
	GoogleAddressPredictionFields,
} from "globalConstants";
import {
	areGeocodeFieldsEmpty,
	checkAddressFieldsEmpty,
	checkIsLatOrLngManual,
} from "helpers";
import { AddressGeocodeType } from "types";
import { AddressDto } from "types/api";

const mapAddressComponents = (addressComponents: { [key: string]: any }[]) => {
	const addressObj = {};

	addressComponents?.forEach(component => {
		const name = component[GoogleAddressComponentsFields.LongName.name];
		const shortName =
			component[GoogleAddressComponentsFields.ShortName.name];

		switch (component.types[0]) {
			case GoogleAddressComponentsFields.StreetNumber.name:
				addressObj[GoogleAddressComponentsFields.StreetNumber.name] =
					name;
				break;
			case GoogleAddressComponentsFields.Street.name:
				addressObj[GoogleAddressComponentsFields.Street.name] = name;
				break;
			// Workaround for cities which part of big agglomerations and don't have locality field from the Google API (e.g. some parts of the New York city)
			case GoogleAddressComponentsFields.Neighborhood.name:
				addressObj[GoogleAddressComponentsFields.City.name] = name;
				break;
			case GoogleAddressComponentsFields.SubCity.name:
				addressObj[GoogleAddressComponentsFields.City.name] = name;
				break;
			case GoogleAddressComponentsFields.City.name:
				addressObj[GoogleAddressComponentsFields.City.name] = name;
				break;
			case GoogleAddressComponentsFields.Region.name:
				addressObj[GoogleAddressComponentsFields.Region.name] =
					shortName;
				break;
			case GoogleAddressComponentsFields.Country.name:
				addressObj[GoogleAddressComponentsFields.Country.name] = name;
				addressObj[GoogleAddressComponentsFields.CountryCode.name] =
					shortName;
				break;
			case GoogleAddressComponentsFields.PostalCode.name:
				addressObj[GoogleAddressComponentsFields.PostalCode.name] =
					name;
				break;
			default:
				break;
		}
	});

	return addressObj;
};

export const mapGeocodeAddress = (data: { [key: string]: any }[]) =>
	data.map(item => ({
		[GoogleAddressOptionFields.Label]:
			item[GoogleAddressPredictionFields.Description] ||
			item[GoogleAddressDetailsFields.FormattedAddress],
		[GoogleAddressOptionFields.PlaceId]:
			item[GoogleAddressPredictionFields.PlaceId],
		[GoogleAddressOptionFields.Address]: mapAddressComponents(
			item[GoogleAddressDetailsFields.AddressComponents],
		),
		[GoogleAddressOptionFields.Lat]:
			item[GoogleAddressDetailsFields.Geometry]?.[
				GoogleAddressDetailsFields.Location
			].lat(),
		[GoogleAddressOptionFields.Lng]:
			item[GoogleAddressDetailsFields.Geometry]?.[
				GoogleAddressDetailsFields.Location
			].lng(),
	}));

const getFullAddress = address => {
	if (address?.longitude && address?.latitude) {
		return `${address?.address_line1 ?? ""}${
			address?.city ? ", " + address?.city : ""
		}${address?.region_code ? ", " + address?.region_code : ""}${
			address?.postal_code ? " " + address?.postal_code : ""
		}${address?.country ? ", " + address?.country : ""}`;
	} else {
		return "";
	}
};

export const mapAddressToApi = (
	data: { [key: string]: any },
	checkEmptyFieldsFn?: (address: AddressGeocodeType) => boolean,
): AddressDto | null => {
	const address: AddressGeocodeType = data?.address;
	const isAddressEmpty = checkEmptyFieldsFn
		? checkEmptyFieldsFn(address)
		: areGeocodeFieldsEmpty(address);

	if (!address || isAddressEmpty) return null;

	const getCountryCode = () => {
		if (typeof address?.country === "object") {
			const value = address?.country?.value;
			return value ? value.toString() : undefined;
		}
		return address?.country_code || undefined;
	};

	return {
		address_line1: address?.route || undefined,
		address_line2: address?.apartments || undefined,
		city: address?.locality || undefined,
		country_code: getCountryCode(),
		latitude: address?.lat,
		location_if_other: address?.location_if_other,
		longitude: address?.lng,
		postal_code: address?.postal_code || undefined,
		region_code: address?.administrative_area_level_1 || undefined,
		type_of_location: address?.type_of_location,
	};
};

export const mapAddressFromApi = (address: AddressDto): any => {
	const isManualAddress =
		checkIsLatOrLngManual({
			lat: address?.latitude,
			lng: address?.longitude,
		}) && !checkAddressFieldsEmpty(address);

	return {
		address: {
			route: address?.address_line1,
			apartments: address?.address_line2,
			locality: address?.city,
			country: address?.country,
			country_code: address?.country_code,
			lat: address?.latitude,
			location_if_other: address?.location_if_other,
			lng: address?.longitude,
			postal_code: address?.postal_code,
			administrative_area_level_1: address?.region_code,
			type_of_location: address?.type_of_location,
			search_address: isManualAddress ? "" : getFullAddress(address),
		},
	};
};
